import { t } from '@glory/i18n';
import type { ReactNode } from 'react';

import { Avatar } from '../components/avatar.tsx';
import { Badge } from '../components/badge.tsx';
import { Field, type FieldPart, splitField } from './field.tsx';
import { AsyncSelect, type AsyncSelectProps } from './select-fields.tsx';

/** Сотрудник в выборе: имя, фото, специализация или должность, статус. */
export interface EmployeeOption {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl?: string | null;
  /** Специализация или должность: «Дизайнер», «Менеджер проектов». */
  readonly role?: string | null;
  readonly status?: 'active' | 'invited' | 'inactive';
}

const STATUS: Record<NonNullable<EmployeeOption['status']>, string | null> = {
  active: null,
  invited: t('fields.employeeInvited'),
  inactive: t('fields.employeeInactive'),
};

export type EmployeePickerProps = Omit<AsyncSelectProps<EmployeeOption>, 'getKey' | 'getLabel' | 'describe'>;

/** Выбор сотрудника: аватар, специализация и статус в строке; поиск — на сервере. */
export function EmployeePicker(props: EmployeePickerProps) {
  return (
    <AsyncSelect
      {...props}
      placeholder={props.placeholder ?? t('fields.employeePlaceholder')}
      getKey={(e) => e.id}
      getLabel={(e) => e.name}
      describe={(e) => ({
        icon: <Avatar name={e.name} src={e.avatarUrl ?? null} size="sm" />,
        description: [e.role, e.status ? STATUS[e.status] : null].filter(Boolean).join(' · ') || undefined,
      })}
    />
  );
}

export interface EmployeePickerFieldProps extends FieldPart, Omit<EmployeePickerProps, keyof FieldPart> {}

export function EmployeePickerField(props: EmployeePickerFieldProps) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <EmployeePicker {...input} />
    </Field>
  );
}

/** Объект в выборе: шаблон, сделка, клиент — с фактами для мини-карточки. */
export interface ObjectOption {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string | null;
  /** Тип объекта для метки: «Шаблон», «Клиент». */
  readonly kind?: string | null;
  readonly icon?: ReactNode;
  /** Короткие факты в мини-карточке: цена, срок, ответственный. */
  readonly facts?: readonly { readonly label: string; readonly value: ReactNode }[];
}

/** Мини-карточка объекта: название, тип, подпись и несколько фактов. */
export function MiniCard({ item }: { item: ObjectOption }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        {item.icon ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-md bg-surface-2 [&_svg]:size-4">
            {item.icon}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="font-bold">{item.title}</span>
          {item.subtitle ? <span className="text-caption text-muted">{item.subtitle}</span> : null}
        </div>
      </div>
      {item.kind ? <Badge className="self-start">{item.kind}</Badge> : null}
      {item.facts && item.facts.length > 0 ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-small">
          {item.facts.map((fact) => (
            <div key={fact.label} className="contents">
              <dt className="text-muted">{fact.label}</dt>
              <dd className="font-semibold">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

export type ObjectPickerProps = Omit<
  AsyncSelectProps<ObjectOption>,
  'getKey' | 'getLabel' | 'describe' | 'renderPreview'
>;

/** Выбор объекта: поиск по объектам, справа — мини-карточка подсвеченного, чтобы не ошибиться. */
export function ObjectPicker(props: ObjectPickerProps) {
  return (
    <AsyncSelect
      {...props}
      getKey={(o) => o.id}
      getLabel={(o) => o.title}
      describe={(o) => ({
        icon: o.icon,
        description: [o.kind, o.subtitle].filter(Boolean).join(' · ') || undefined,
      })}
      renderPreview={(o) => <MiniCard item={o} />}
    />
  );
}

export interface ObjectPickerFieldProps extends FieldPart, Omit<ObjectPickerProps, keyof FieldPart> {}

export function ObjectPickerField(props: ObjectPickerFieldProps) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <ObjectPicker {...input} />
    </Field>
  );
}
